import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IImage, IProductViewModel } from '@ncg/data';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IProductGalleryItem } from '../../../models/product-gallery.interface';
import { DialogService } from '../../../utils/dialog/dialog.service';
import { ImageUrl } from '../../../utils/helpers/image-helper';
import { TrackingService } from '../../../core/tracking.service';
import { ProductGalleryCarouselComponent } from './product-gallery-carousel.component';

@Component({
    selector: 'ncg-product-gallery',
    template: `<ng-container *ngIf="media.length; else noPictures">
            <div class="product-gallery__wrapper is-relative is-hidden-mobile">
                <figure [class.is-grid]="!isOne" class="product-gallery__figure-grid">
                    <picture
                        [ngClass]="{
                            'is-one': isOne,
                            'is-video': item.type === 'video' && hasConsent,
                            'is-clickable': item.type === 'image' || (item.type === 'video' && hasConsent)
                        }"
                        class="product-gallery__picture"
                        *ngFor="let item of prominentMedia; let index = index; let isFirst = first"
                        (click)="openOverlay(index, item.type === 'video')"
                    >
                        <img
                            *ngIf="isFirst"
                            [attr.alt]="product.properties.displayName + ' - ' + index"
                            [attr.title]="product.properties.displayName"
                            [attr.src]="item.thumbnail | productImage: 'small'"
                            [attr.srcset]="(item.thumbnail | productImage: 'small') + ' 400w,' + (item.thumbnail | productImage: 'medium') + ' 800w'"
                            sizes="100vw"
                            width="960"
                            height="640"
                            loading="lazy"
                            ncgImageLoad
                        />
                        <img
                            *ngIf="!isFirst"
                            [attr.alt]="product.properties.displayName + ' - ' + index"
                            [attr.title]="product.properties.displayName"
                            [attr.src]="item.thumbnail | productImage: 'small'"
                            width="400"
                            height="267"
                            loading="lazy"
                            ncgImageLoad
                        />
                        <div
                            class="product-gallery__overlay is-flex is-align-items-center is-justify-content-center has-text-centered"
                            *ngIf="item.type !== 'video'"
                        >
                            <p class="product-gallery__overlay--text">
                                <ng-container *ngIf="media.length > 1; else singularGalleryTmpl">
                                    {{ 'product_details.gallery_see_all' | translate: { amount: media.length } }}
                                </ng-container>
                                <ng-template #singularGalleryTmpl>
                                    {{ 'product_details.gallery_see_all_singular' | translate }}
                                </ng-template>
                            </p>
                        </div>

                        <div class="product-gallery__video-blocked" *ngIf="item.type === 'video' && !hasConsent">
                            <p class="pb-3 has-text-centered">{{ 'embed_spot.video_blocked_placeholder' | translate }}</p>
                            <button class="button is-primary" (click)="onShowCookieBanner()" type="button">
                                {{ 'embed_spot.open_cookie' | translate }}
                            </button>
                        </div>
                    </picture>
                </figure>
            </div>
            <div class="is-hidden-tablet">
                <ncg-product-gallery-mobile-carousel [media]="media" [altText]="product.properties.displayName || fallbackAlt || 'product image'">
                </ncg-product-gallery-mobile-carousel>
            </div>
        </ng-container>

        <ng-template #noPictures>
            <div *ngIf="fallbackSrc" class="container is-fullwidth product-gallery__figure-grid-fallback">
                <picture class="is-one">
                    <source media="(max-width: 767px)" [srcset]="fallbackMobileSrcSet" />
                    <source media="(min-width: 768px)" [srcset]="fallbackDesktopSrc" />
                    <img [attr.alt]="fallbackAlt" [src]="fallbackSrc" width="1600" height="500" sizes="100vw" loading="lazy" />
                </picture>
                <h2 class="product-gallery__figure-grid-fallback-text">{{ 'product_details.gallery_placeholder' | translate }}</h2>
            </div>
        </ng-template> `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGalleryComponent implements OnInit, OnDestroy {
    @Input() product: IProductViewModel;
    @Input() set fallbackImage(value: IImage | undefined) {
        if (value) {
            this.fallbackMobileSrcSet = `
                ${ImageUrl(value, { width: 800, height: 400, mode: 'crop' })} 1x,
                ${ImageUrl(value, { width: 1600, height: 800, mode: 'crop' })} 2x
                `;
            this.fallbackDesktopSrc = `
                ${ImageUrl(value, { width: 1600, height: 500, mode: 'crop' })} 1x,
                ${ImageUrl(value, { width: 3200, height: 1000, mode: 'crop' })} 2x
                `;

            this.fallbackSrc = ImageUrl(value, { width: 1600, height: 500, mode: 'crop' });
            this.fallbackAlt = value.altText;
        } else {
            this.fallbackMobileSrcSet = '';
            this.fallbackDesktopSrc = '';
            this.fallbackSrc = '';
            this.fallbackAlt = undefined;
        }
    }

    media: IProductGalleryItem[] = [];
    prominentMedia: IProductGalleryItem[];
    isOne: boolean;
    fallbackDesktopSrc = '';
    fallbackMobileSrcSet = '';
    fallbackSrc = '';
    fallbackAlt?: string;
    hasConsent = false;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private readonly dialogService: DialogService,
        private readonly tracking: TrackingService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._initWithConsent();
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    openOverlay(index = 0, isVideo = false): void {
        if (isVideo && !this.hasConsent) {
            return;
        }

        if (this.media.length) {
            this.dialogService.openDialogWithComponent(ProductGalleryCarouselComponent, {
                fullHeight: true,
                blockScroll: true,
                hasBackdrop: true,
                data: {
                    media: this.media,
                    mediaIndex: index,
                },
                positionHorizontal: {
                    placement: 'center',
                },
            });
        }
    }

    onShowCookieBanner() {
        this.tracking.showCookieBanner();
    }

    private generateThumbnail(url: string): string {
        const pattern = /^https?:\/\/(.*?)\//i;
        const matches = pattern.exec(url);

        let thumbnail = url;

        switch (matches ? matches[1] : 'none') {
            case 'view.vzaar.com':
                thumbnail = url.replace('player', 'image');
                break;
        }

        return thumbnail;
    }

    private _initWithConsent(): void {
        const images = this.product.properties.pictures;

        if (images && Array.isArray(images)) {
            this.media = images.map<IProductGalleryItem>((src) => {
                const thumbnail = this.generateThumbnail(src);
                return {
                    type: 'image',
                    src,
                    thumbnail,
                };
            });

            this._addVideo();
            this._setProminentMedia();
        }

        this.tracking
            .marketingConsentGiven()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((consent) => {
                this.hasConsent = consent;
                this.cd.markForCheck();
            });
    }

    private _addVideo(): void {
        if (this.product.properties.video) {
            const thumbnail = this.generateThumbnail(this.product.properties.video);
            this.media.splice(4, 0, {
                type: 'video',
                src: this.product.properties.video,
                thumbnail,
            });
        }
    }

    private _setProminentMedia(): void {
        this.prominentMedia = this.media.slice(0, 5);
        this.isOne = this.prominentMedia.length <= 1;
    }
}
